import { ChevronDown, ChevronRight, GripVertical, Edit2, Trash2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React from 'react';

interface PlanHeaderCardProps {
  id: number;
  name: string;
  eventCount: number;
  førProduksjonsstart?: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onEdit: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
  activeElementId?: number | null; // For drop zone indicator
  activeElementType?: 'overskrift' | 'hendelse' | 'musikk_placeholder' | null;
  className?: string;
}

export function PlanHeaderCard({
  id,
  name,
  eventCount,
  førProduksjonsstart = false,
  isExpanded,
  onToggle,
  onEdit,
  onDelete,
  activeElementId = null,
  activeElementType = null,
  className
}: PlanHeaderCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const showDropZone = isOver && (activeElementType === 'hendelse' || activeElementType === 'musikk_placeholder') && activeElementId !== id;
  const showOverskriftDropIndicator = isOver && activeElementType === 'overskrift' && activeElementId !== id;

  return (
    <div className="relative">
      {/* Drop indicator for overskrift reordering */}
      {showOverskriftDropIndicator && (
        <div className="absolute -top-1 left-0 right-0 h-1 bg-primary z-50 rounded-full shadow-lg">
          <div className="absolute left-1/2 -translate-x-1/2 -top-1.5 w-4 h-4 bg-primary rounded-full border-2 border-white shadow-lg" />
        </div>
      )}
      
      <div ref={setNodeRef} style={style}>
        <div
          className={cn(
            'bg-primary text-primary-foreground transition-all',
            isDragging ? 'opacity-0' : '',
            showDropZone ? 'ring-4 ring-green-400 ring-inset' : '',
            className
          )}
        >
          <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3">
            {/* Drag handle */}
            <div 
              {...attributes} 
              {...listeners}
              className="cursor-grab active:cursor-grabbing text-primary-foreground/70 hover:text-primary-foreground flex items-center shrink-0"
            >
              <GripVertical className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>

            {/* Expand/collapse */}
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggle}
              className="h-6 w-6 sm:h-8 sm:w-8 text-primary-foreground hover:bg-primary-foreground/20 shrink-0"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5" />
              ) : (
                <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
              )}
            </Button>

            {/* Overskrift navn */}
            <h3 className="flex-1 font-semibold text-sm sm:text-base truncate min-w-0">{name}</h3>

            {/* Før produksjonsstart indicator */}
            {førProduksjonsstart && (
              <Badge 
                variant="secondary" 
                className="bg-amber-100 text-amber-800 border-amber-200 text-xs shrink-0 gap-1 hidden xs:flex"
              >
                <Clock className="h-3 w-3" />
                <span>Før start</span>
              </Badge>
            )}
            {førProduksjonsstart && (
              <div className="xs:hidden shrink-0 flex items-center justify-center w-6 h-6 bg-amber-100 rounded-full" title="Før produksjonsstart">
                <Clock className="h-3.5 w-3.5 text-amber-600" />
              </div>
            )}

            {/* Innslag count badge */}
            <Badge 
              variant="secondary" 
              className="bg-primary-foreground/20 text-primary-foreground border-primary-foreground/30 text-xs shrink-0"
            >
              {eventCount}
            </Badge>

            {/* Actions */}
            <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
              <Button
                variant="ghost"
                size="icon"
                onClick={onEdit}
                className="h-6 w-6 sm:h-8 sm:w-8 text-primary-foreground hover:bg-primary-foreground/20"
              >
                <Edit2 className="h-3 w-3 sm:h-4 sm:w-4" />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={onDelete}
                className="h-6 w-6 sm:h-8 sm:w-8 text-primary-foreground hover:bg-primary-foreground/20"
              >
                <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

